// src/components/finance/ReceiptOptionsDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Stack,
  Zoom
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  ReceiptLong as TicketIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon
} from '@mui/icons-material';

const ReceiptOptionsDialog = ({ open, onClose, invoiceId, invoiceNumber, amount, clientEmail }) => {
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // 'success' | 'error' | null
  const [emailMessage, setEmailMessage] = useState('');
  const [emailFormat, setEmailFormat] = useState('ticket');

  const handleSendEmail = async () => {
    setSendingEmail(true);
    setEmailStatus(null);
    setEmailMessage('');

    try {
      const response = await fetch(`/api/invoices/${invoiceId}/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ format: emailFormat })
      });

      const data = await response.json();

      if (response.ok) {
        setEmailStatus('success');
        setEmailMessage(`¡Comprobante enviado exitosamente a ${clientEmail}!`);
      } else {
        throw new Error(data.message || 'Error al enviar el correo');
      }
    } catch (err) {
      console.error('[Send Email Error]', err);
      setEmailStatus('error');
      setEmailMessage(err.message || 'No se pudo enviar el correo.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePrint = (format) => {
    const token = localStorage.getItem('token');
    const printUrl = `/api/invoices/${invoiceId}/download?format=${format}&inline=true${format === 'ticket' ? '&html=true' : ''}&t=${Date.now()}${token ? `&token=${token}` : ''}`;
    
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = printUrl;
    
    document.body.appendChild(iframe);
    
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Error triggering print in iframe:', e);
        window.open(printUrl, '_blank');
      }
      
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 5000);
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          p: 2
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
          ¡Pago Registrado!
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 3 }}>
          <Zoom in={true} style={{ transitionDelay: '100ms' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
          </Zoom>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Comprobante N° {invoiceNumber}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#6366f1', mt: 0.5, mb: 1 }}>
            S/ {parseFloat(amount || 0).toFixed(2)}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', px: 2 }}>
            El abono se ha procesado y conciliado en la base de datos de manera correcta.
          </Typography>
        </Box>

        {/* Sección Envío por Correo */}
        <Box sx={{ p: 2, borderRadius: '16px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 18, color: '#6366f1' }} /> Enviar al Cliente
          </Typography>

          {clientEmail ? (
            <Stack spacing={1.5}>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                Destinatario: <strong>{clientEmail}</strong>
              </Typography>

              <TextField
                fullWidth
                select
                size="small"
                label="Formato de Adjunto"
                value={emailFormat}
                onChange={(e) => setEmailFormat(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              >
                <MenuItem value="ticket">Ticket Térmico (80mm)</MenuItem>
                <MenuItem value="normal">Documento Normal (A4)</MenuItem>
              </TextField>

              <Button
                variant="contained"
                onClick={handleSendEmail}
                disabled={sendingEmail}
                fullWidth
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  backgroundColor: '#6366f1',
                  boxShadow: '0 4px 10px rgba(99, 102, 241, 0.15)',
                  '&:hover': { backgroundColor: '#4f46e5' }
                }}
              >
                {sendingEmail ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    <span>Enviando...</span>
                  </Box>
                ) : (
                  'Enviar Comprobante por Correo'
                )}
              </Button>
            </Stack>
          ) : (
            <Alert severity="warning" sx={{ borderRadius: '10px', p: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
              El cliente no tiene un correo electrónico registrado en su perfil.
            </Alert>
          )}

          {emailStatus && (
            <Alert
              severity={emailStatus}
              sx={{ borderRadius: '10px', mt: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
            >
              {emailMessage}
            </Alert>
          )}
        </Box>

        {/* Sección Descargas Directas */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrintIcon sx={{ fontSize: 18, color: '#0ea5e9' }} /> Imprimir Comprobante
          </Typography>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<TicketIcon />}
              onClick={() => handlePrint('ticket')}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                borderColor: '#e2e8f0',
                color: '#334155',
                '&:hover': { borderColor: '#cbd5e0', bgcolor: '#f1f5f9' }
              }}
            >
              Ticket (80mm)
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PdfIcon />}
              onClick={() => handlePrint('normal')}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                borderColor: '#e2e8f0',
                color: '#334155',
                '&:hover': { borderColor: '#cbd5e0', bgcolor: '#f1f5f9' }
              }}
            >
              Normal (A4)
            </Button>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            py: 1.2,
            backgroundColor: '#0f172a',
            '&:hover': { backgroundColor: '#1e293b' }
          }}
        >
          Entendido / Salir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptOptionsDialog;
